import { lazy, Suspense, type ReactNode } from 'react';
import { useWorkforceHiveEnabled } from '@/hooks/useWorkforceHiveEnabled';
import { Skeleton } from '@/components/ui/skeleton';

const NotFound = lazy(() => import('@/pages/NotFound'));

/**
 * Runtime gate for every public /workforce-hive* route.
 *
 * - While the runtime flag is loading: render a neutral skeleton (no Hive UI,
 *   no flash of disabled state, no flash of enabled state).
 * - When the flag is OFF (or fails closed on error): render the canonical
 *   <NotFound /> page so direct deep links behave identically to a
 *   never-registered route.
 * - When the flag is ON: render the wrapped Hive page.
 */
export function HiveRouteGate({ children }: { children: ReactNode }) {
  const { enabled, loading } = useWorkforceHiveEnabled();

  if (loading) {
    return (
      <main className="container mx-auto flex min-h-[60vh] flex-col gap-4 px-4 py-12">
        <Skeleton className="h-8 w-2/3 max-w-md" />
        <Skeleton className="h-4 w-1/2 max-w-sm" />
        <Skeleton className="h-64 w-full" />
      </main>
    );
  }

  if (!enabled) {
    return (
      <Suspense fallback={null}>
        <NotFound />
      </Suspense>
    );
  }

  return <>{children}</>;
}